import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  buildWorkflowSummary,
  creatorKeyOf,
  CREATOR_WORKFLOW_DEFAULTS,
  mergeCreatorsWithWorkflows,
  norm,
  validateWorkflowDraft,
  WORKFLOW_ROW_ERROR,
  WORKFLOW_ROW_IDLE,
  WORKFLOW_ROW_SAVED,
  WORKFLOW_ROW_SAVING,
} from "../features/creatorWorkflow/constants";
import {
  bulkUpsertCreatorWorkflows,
  getCreatorWorkflows,
  upsertCreatorWorkflow,
} from "../services/creatorWorkflowService";

/* ────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────── */
function ensureCampaignState(state, campaignId) {
  const id = norm(campaignId);

  if (!state.byCampaignId[id]) {
    state.byCampaignId[id] = {
      items: [],
      drafts: {},
      rowState: {},
      loading: false,
      error: "",
      loaded: false,
      lastLoadedAt: 0,
    };
  }

  return state.byCampaignId[id];
}

function buildDraftMapFromItems(items = []) {
  const out = {};

  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = creatorKeyOf(item?.creatorKey || item?.username || item?.displayName);
    if (!key) return;

    out[key] = {
      ...CREATOR_WORKFLOW_DEFAULTS,
      ...item,
      creatorKey: key,
    };
  });

  return out;
}

function upsertItem(items = [], nextItem) {
  const key = creatorKeyOf(nextItem?.creatorKey || nextItem?.username || nextItem?.displayName);
  if (!key) return Array.isArray(items) ? [...items] : [];

  const list = Array.isArray(items) ? [...items] : [];
  const index = list.findIndex(
    (x) => creatorKeyOf(x?.creatorKey || x?.username || x?.displayName) === key
  );

  if (index >= 0) {
    list[index] = {
      ...list[index],
      ...nextItem,
      creatorKey: key,
    };
    return list;
  }

  return [
    ...list,
    {
      ...nextItem,
      creatorKey: key,
    },
  ];
}

function replaceItems(existing = [], nextItems = []) {
  let list = Array.isArray(existing) ? [...existing] : [];

  (Array.isArray(nextItems) ? nextItems : []).forEach((item) => {
    list = upsertItem(list, item);
  });

  return list;
}

function defaultRowState() {
  return {
    status: WORKFLOW_ROW_IDLE,
    error: "",
    savedAt: 0,
  };
}

/* ────────────────────────────────────────────────
   Thunks
───────────────────────────────────────────────── */
export const fetchCreatorWorkflowsAsync = createAsyncThunk(
  "creatorWorkflow/fetchCreatorWorkflows",
  async ({ campaignId }, thunkApi) => {
    try {
      const response = await getCreatorWorkflows(campaignId);

      return {
        campaignId: norm(campaignId),
        items: Array.isArray(response?.items) ? response.items : [],
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error?.message || "Failed to load creator workflow.");
    }
  }
);

export const saveCreatorWorkflowAsync = createAsyncThunk(
  "creatorWorkflow/saveCreatorWorkflow",
  async ({ campaignId, creatorKey, payload }, thunkApi) => {
    try {
      const errors = validateWorkflowDraft({
        creatorKey,
        ...payload,
      });

      if (Object.keys(errors).length) {
        const firstError = Object.values(errors)[0] || "Invalid creator workflow row.";
        return thunkApi.rejectWithValue({
          campaignId: norm(campaignId),
          creatorKey: creatorKeyOf(creatorKey),
          message: firstError,
          validationErrors: errors,
        });
      }

      const saved = await upsertCreatorWorkflow(campaignId, creatorKey, payload);

      return {
        campaignId: norm(campaignId),
        item: saved,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({
        campaignId: norm(campaignId),
        creatorKey: creatorKeyOf(creatorKey),
        message: error?.message || "Failed to save creator workflow.",
      });
    }
  }
);

export const bulkSaveCreatorWorkflowsAsync = createAsyncThunk(
  "creatorWorkflow/bulkSaveCreatorWorkflows",
  async ({ campaignId, items }, thunkApi) => {
    try {
      const response = await bulkUpsertCreatorWorkflows(campaignId, items);

      return {
        campaignId: norm(campaignId),
        items: Array.isArray(response?.items) ? response.items : [],
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error?.message || "Failed to save creator workflows.");
    }
  }
);

/* ────────────────────────────────────────────────
   Slice
───────────────────────────────────────────────── */
const initialState = {
  byCampaignId: {},
};

const creatorWorkflowSlice = createSlice({
  name: "creatorWorkflow",
  initialState,
  reducers: {
    hydrateCreatorWorkflowDrafts(state, action) {
      const { campaignId, creators = [] } = action.payload || {};
      const bucket = ensureCampaignState(state, campaignId);

      const merged = mergeCreatorsWithWorkflows(creators, bucket.items);
      const mergedDrafts = buildDraftMapFromItems(merged);
      const itemDrafts = buildDraftMapFromItems(bucket.items);
      const currentDrafts = bucket.drafts || {};
      const hydratedDrafts = {};

      const allKeys = new Set([
        ...Object.keys(mergedDrafts),
        ...Object.keys(itemDrafts),
        ...Object.keys(currentDrafts),
      ]);

      allKeys.forEach((key) => {
        hydratedDrafts[key] = {
          ...CREATOR_WORKFLOW_DEFAULTS,
          ...(mergedDrafts[key] || {}),
          ...(itemDrafts[key] || {}),
          ...(currentDrafts[key] || {}),
          creatorKey: key,
        };
      });

      bucket.drafts = hydratedDrafts;

      Object.keys(hydratedDrafts).forEach((key) => {
        if (!bucket.rowState[key]) {
          bucket.rowState[key] = defaultRowState();
        }
      });
    },

    updateCreatorWorkflowDraft(state, action) {
      const { campaignId, creatorKey, field, value } = action.payload || {};
      const bucket = ensureCampaignState(state, campaignId);
      const key = creatorKeyOf(creatorKey);

      if (!key) return;

      if (!bucket.drafts[key]) {
        bucket.drafts[key] = {
          creatorKey: key,
          ...CREATOR_WORKFLOW_DEFAULTS,
        };
      }

      bucket.drafts[key] = {
        ...bucket.drafts[key],
        [field]: value,
      };

      if (!bucket.rowState[key]) {
        bucket.rowState[key] = defaultRowState();
      } else if (bucket.rowState[key].status === WORKFLOW_ROW_ERROR) {
        bucket.rowState[key].error = "";
        bucket.rowState[key].status = WORKFLOW_ROW_IDLE;
      }
    },

    replaceCreatorWorkflowDraft(state, action) {
      const { campaignId, creatorKey, draft } = action.payload || {};
      const bucket = ensureCampaignState(state, campaignId);
      const key = creatorKeyOf(creatorKey);

      if (!key) return;

      bucket.drafts[key] = {
        ...CREATOR_WORKFLOW_DEFAULTS,
        ...(draft || {}),
        creatorKey: key,
      };

      if (!bucket.rowState[key]) {
        bucket.rowState[key] = defaultRowState();
      }
    },

    resetCreatorWorkflowRowState(state, action) {
      const { campaignId, creatorKey } = action.payload || {};
      const bucket = ensureCampaignState(state, campaignId);
      const key = creatorKeyOf(creatorKey);

      if (!key) return;

      bucket.rowState[key] = defaultRowState();
    },

    clearCreatorWorkflowError(state, action) {
      const campaignId = norm(action.payload?.campaignId);
      const bucket = ensureCampaignState(state, campaignId);
      bucket.error = "";
    },

    clearCreatorWorkflowCampaignState(state, action) {
      const campaignId = norm(action.payload?.campaignId);
      if (campaignId && state.byCampaignId[campaignId]) {
        delete state.byCampaignId[campaignId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreatorWorkflowsAsync.pending, (state, action) => {
        const bucket = ensureCampaignState(state, action.meta.arg?.campaignId);
        bucket.loading = true;
        bucket.error = "";
      })

      .addCase(fetchCreatorWorkflowsAsync.fulfilled, (state, action) => {
        const { campaignId, items } = action.payload;
        const bucket = ensureCampaignState(state, campaignId);
        const safeItems = Array.isArray(items) ? items : [];

        bucket.loading = false;
        bucket.error = "";
        bucket.loaded = true;
        bucket.lastLoadedAt = Date.now();
        bucket.items = safeItems;

        const fetchedDrafts = buildDraftMapFromItems(safeItems);
        const currentDrafts = bucket.drafts || {};
        const nextDrafts = { ...currentDrafts };

        Object.keys(fetchedDrafts).forEach((key) => {
          const current = currentDrafts[key] || {};
          const rowState = bucket.rowState?.[key];
          const isSaving = rowState?.status === WORKFLOW_ROW_SAVING;
          const isError = rowState?.status === WORKFLOW_ROW_ERROR;

          nextDrafts[key] = isSaving || isError
            ? {
                ...fetchedDrafts[key],
                ...current,
                creatorKey: key,
              }
            : {
                ...current,
                ...fetchedDrafts[key],
                creatorKey: key,
              };

          if (!bucket.rowState[key]) {
            bucket.rowState[key] = defaultRowState();
          }
        });

        bucket.drafts = nextDrafts;
      })

      .addCase(fetchCreatorWorkflowsAsync.rejected, (state, action) => {
        const bucket = ensureCampaignState(state, action.meta.arg?.campaignId);
        bucket.loading = false;
        bucket.error =
          (typeof action.payload === "string" && action.payload) ||
          action.error?.message ||
          "Failed to load creator workflow.";
      })

      .addCase(saveCreatorWorkflowAsync.pending, (state, action) => {
        const { campaignId, creatorKey } = action.meta.arg || {};
        const bucket = ensureCampaignState(state, campaignId);
        const key = creatorKeyOf(creatorKey);

        if (!key) return;

        bucket.rowState[key] = {
          ...(bucket.rowState[key] || defaultRowState()),
          status: WORKFLOW_ROW_SAVING,
          error: "",
        };
      })

      .addCase(saveCreatorWorkflowAsync.fulfilled, (state, action) => {
        const { campaignId, item } = action.payload;
        const bucket = ensureCampaignState(state, campaignId);
        const key = creatorKeyOf(item?.creatorKey || item?.username || item?.displayName);

        if (!key) return;

        bucket.items = upsertItem(bucket.items, item);

        bucket.drafts[key] = {
          ...CREATOR_WORKFLOW_DEFAULTS,
          ...(bucket.drafts[key] || {}),
          ...item,
          creatorKey: key,
        };

        bucket.rowState[key] = {
          status: WORKFLOW_ROW_SAVED,
          error: "",
          savedAt: Date.now(),
        };
      })

      .addCase(saveCreatorWorkflowAsync.rejected, (state, action) => {
        const payload = action.payload || {};
        const campaignId = payload.campaignId || action.meta.arg?.campaignId;
        const creatorKey = payload.creatorKey || action.meta.arg?.creatorKey;
        const bucket = ensureCampaignState(state, campaignId);
        const key = creatorKeyOf(creatorKey);

        if (!key) return;

        bucket.rowState[key] = {
          ...(bucket.rowState[key] || defaultRowState()),
          status: WORKFLOW_ROW_ERROR,
          error: payload.message || action.error?.message || "Failed to save creator workflow.",
          savedAt: 0,
        };
      })

      .addCase(bulkSaveCreatorWorkflowsAsync.pending, (state, action) => {
        const bucket = ensureCampaignState(state, action.meta.arg?.campaignId);
        const items = Array.isArray(action.meta.arg?.items) ? action.meta.arg.items : [];

        items.forEach((item) => {
          const key = creatorKeyOf(item?.creatorKey || item?.username || item?.displayName);
          if (!key) return;

          bucket.rowState[key] = {
            ...(bucket.rowState[key] || defaultRowState()),
            status: WORKFLOW_ROW_SAVING,
            error: "",
          };
        });
      })

      .addCase(bulkSaveCreatorWorkflowsAsync.fulfilled, (state, action) => {
        const { campaignId, items } = action.payload;
        const bucket = ensureCampaignState(state, campaignId);

        bucket.items = replaceItems(bucket.items, items);

        (Array.isArray(items) ? items : []).forEach((item) => {
          const key = creatorKeyOf(item?.creatorKey || item?.username || item?.displayName);
          if (!key) return;

          bucket.drafts[key] = {
            ...CREATOR_WORKFLOW_DEFAULTS,
            ...(bucket.drafts[key] || {}),
            ...item,
            creatorKey: key,
          };

          bucket.rowState[key] = {
            status: WORKFLOW_ROW_SAVED,
            error: "",
            savedAt: Date.now(),
          };
        });
      })

      .addCase(bulkSaveCreatorWorkflowsAsync.rejected, (state, action) => {
        const bucket = ensureCampaignState(state, action.meta.arg?.campaignId);
        bucket.error =
          (typeof action.payload === "string" && action.payload) ||
          action.error?.message ||
          "Failed to save creator workflows.";
      });
  },
});

export const {
  hydrateCreatorWorkflowDrafts,
  updateCreatorWorkflowDraft,
  replaceCreatorWorkflowDraft,
  resetCreatorWorkflowRowState,
  clearCreatorWorkflowError,
  clearCreatorWorkflowCampaignState,
} = creatorWorkflowSlice.actions;

export default creatorWorkflowSlice.reducer;

/* ────────────────────────────────────────────────
   Selectors
───────────────────────────────────────────────── */
const selectCreatorWorkflowRoot = (state) => state.creatorWorkflow || initialState;

export const selectCreatorWorkflowCampaignState = (state, campaignId) => {
  const id = norm(campaignId);

  return (
    selectCreatorWorkflowRoot(state).byCampaignId?.[id] || {
      items: [],
      drafts: {},
      rowState: {},
      loading: false,
      error: "",
      loaded: false,
      lastLoadedAt: 0,
    }
  );
};

export const selectCreatorWorkflowItems = (state, campaignId) =>
  selectCreatorWorkflowCampaignState(state, campaignId).items || [];

export const selectCreatorWorkflowDrafts = (state, campaignId) =>
  selectCreatorWorkflowCampaignState(state, campaignId).drafts || {};

export const selectCreatorWorkflowRowStateMap = (state, campaignId) =>
  selectCreatorWorkflowCampaignState(state, campaignId).rowState || {};

export const selectCreatorWorkflowLoading = (state, campaignId) =>
  Boolean(selectCreatorWorkflowCampaignState(state, campaignId).loading);

export const selectCreatorWorkflowError = (state, campaignId) =>
  selectCreatorWorkflowCampaignState(state, campaignId).error || "";

export const selectCreatorWorkflowLoaded = (state, campaignId) =>
  Boolean(selectCreatorWorkflowCampaignState(state, campaignId).loaded);

export const selectCreatorWorkflowMergedRows = (state, campaignId, creators = []) => {
  const bucket = selectCreatorWorkflowCampaignState(state, campaignId);
  const drafts = bucket.drafts || {};
  const values = Object.values(drafts);

  if (values.length) {
    return mergeCreatorsWithWorkflows(creators, values);
  }

  return mergeCreatorsWithWorkflows(creators, bucket.items || []);
};

export const selectCreatorWorkflowSummary = (state, campaignId) => {
  const drafts = selectCreatorWorkflowDrafts(state, campaignId);
  return buildWorkflowSummary(Object.values(drafts || {}));
};

export const selectCreatorWorkflowRowState = (state, campaignId, creatorKey) => {
  const map = selectCreatorWorkflowRowStateMap(state, campaignId);
  const key = creatorKeyOf(creatorKey);

  return map[key] || defaultRowState();
};